import { useState } from 'react'

import { useQuery } from '@tanstack/react-query'

import { conRespaldo } from '@/lib/campo/loUltimoQueSeSupo'
import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { OrdenPorCotizar } from '@/lib/supabase/tipos-bd'
import { Cotizador } from '@/modules/cotizador/Cotizador'
import { cuandoEs, duracion } from '@/modules/visitas/horario'
import { RegistrarVisita } from '@/modules/visitas/RegistrarVisita'

import { useVisitasEnCola } from './useColaDeAcciones'

import estilos from './PorCotizar.module.css'

/**
 * LO QUE LE ENCARGARON IR A COTIZAR.
 *
 * Antes esto se pedía por teléfono: «pásese por lo de la señora Yolanda y me
 * dice cuánto vale». La oficina no sabía quién había ido ni cuándo, y el
 * técnico llegaba sin la dirección ni lo que el cliente había pedido.
 *
 * Es una carta como la del trabajo del día, pero NO es un trabajo: no se
 * marca llegada ni se cierra nada. Lo único que hay que hacer con ella es
 * ponerle precio, así que lo único que ofrece es cotizar.
 */
export function useMisOrdenesPorCotizar() {
  const consulta = useQuery({
    queryKey: ['mis-ordenes-por-cotizar'],
    staleTime: 60 * 1000,
    // Se refresca sola (0256): con el teléfono abierto desde temprano, una
    // visita programada a media mañana no aparecía hasta reiniciar la app.
    // Le pasó a Brallan con la OS-2026-0101.
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
    // Se guarda lo último: el encargo hay que poder verlo en la obra, que es
    // donde no hay señal.
    queryFn: () =>
      conRespaldo<OrdenPorCotizar[]>('mis-ordenes-por-cotizar', async () => {
        const { data, error } = await supabase.rpc('fn_mis_ordenes_por_cotizar')

        if (error) throw new Error(mensajeDeError(error))

        return ((data ?? []) as unknown as OrdenPorCotizar[]).map((o) => ({
          ...o,
          latitud: o.latitud === null ? null : Number(o.latitud),
          longitud: o.longitud === null ? null : Number(o.longitud),
          renglones: Number(o.renglones ?? 0),
          visita_horas: o.visita_horas === null ? null : Number(o.visita_horas),
        }))
      }),
  })

  return { ordenes: consulta.data ?? [], cargando: consulta.isLoading }
}

export function PorCotizar() {
  const { ordenes: todas } = useMisOrdenesPorCotizar()
  // Las visitas registradas sin señal cuentan como hechas (0256): si el
  // cliente no quiso cotización la orden sale de la lista; si quiso, queda
  // para cotizar. Lo dice el teléfono hasta que la base se entere.
  const enCola = useVisitasEnCola()
  const ordenes = todas
    .filter(
      (o) => !enCola.some((v) => v.ordenServicioId === o.orden_servicio_id && !v.quiereCotizar),
    )
    .map((o) =>
      enCola.some((v) => v.ordenServicioId === o.orden_servicio_id) && !o.visita_hecha_en
        ? { ...o, visita_hecha_en: 'en-el-telefono', visita_quiere_cotizar: true }
        : o,
    )
  const [cotizando, setCotizando] = useState<OrdenPorCotizar | null>(null)
  const [visitando, setVisitando] = useState<OrdenPorCotizar | null>(null)

  if (ordenes.length === 0) return null

  const conVisita = ordenes.some((o) => o.visita_inicio)

  return (
    <section className={estilos.zona}>
      <h2 className={estilos.titulo}>
        {conVisita ? 'Tus visitas' : 'Para cotizar'}
        {ordenes.length > 1 && ` · ${ordenes.length}`}
      </h2>
      <p className={estilos.subtitulo}>
        Te las programaron para que vayas, mires y pongas el precio.
      </p>

      <ul className={estilos.lista}>
        {ordenes.map((o) => (
          <li key={o.orden_servicio_id} className={estilos.tarjeta}>
            <div className={estilos.encabezado}>
              <span className={estilos.numero}>{o.numero}</span>
              {o.servicio_codigo && <span className={estilos.servicio}>{o.servicio_codigo}</span>}
              {/* Si ya la empezó, que se vea: para no volver a armarla de cero. */}
              {o.renglones > 0 && (
                <span className={estilos.empezada}>
                  {o.renglones === 1 ? '1 renglón' : `${o.renglones} renglones`}
                </span>
              )}
            </div>

            {/* Cuándo es la visita (0256): lo primero que se busca. */}
            {o.visita_inicio && (
              <p className={estilos.cuando}>
                {cuandoEs(o.visita_inicio)} · {duracion(o.visita_horas)}
              </p>
            )}
            {o.visita_hecha_en && (
              <p className={estilos.hecha}>
                {o.visita_hecha_en === 'en-el-telefono'
                  ? 'Visita guardada en el teléfono · el cliente quiere cotización'
                  : 'Visita hecha · el cliente quiere cotización'}
              </p>
            )}

            <p className={estilos.cliente}>{o.cliente ?? 'Sin cliente'}</p>
            <p className={estilos.direccion}>{o.direccion}</p>

            {/* 0302: gerencia se la devolvió: qué le faltó. */}
            {o.motivo_devolucion && (
              <p className={estilos.devuelta}>
                <b>Te la devolvieron:</b> {o.motivo_devolucion}
              </p>
            )}

            {o.descripcion && <p className={estilos.queHacer}>{o.descripcion}</p>}

            <div className={estilos.acciones}>
              {/* Cómo llegar y a quién llamar: lo mismo que lleva la tarjeta
                  del trabajo, porque hace la misma falta. */}
              {o.latitud !== null && o.longitud !== null && (
                <a
                  className={estilos.enlace}
                  href={`https://www.google.com/maps/dir/?api=1&destination=${o.latitud},${o.longitud}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Cómo llegar
                </a>
              )}
              {o.telefono && (
                <a className={estilos.enlace} href={`tel:${o.telefono}`}>
                  Llamar · {o.telefono}
                </a>
              )}

              {/* La de certificación RETIE se registra primero: fotos,
                  observación y si el cliente quiere cotización (0256). */}
              {o.es_certificacion && !o.visita_hecha_en ? (
                <button type="button" className={estilos.cotizar} onClick={() => setVisitando(o)}>
                  Registrar visita
                </button>
              ) : (
                <button type="button" className={estilos.cotizar} onClick={() => setCotizando(o)}>
                  {o.renglones > 0 ? 'Seguir cotizando' : 'Cotizar'}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      {visitando && (
        <RegistrarVisita
          ordenServicioId={visitando.orden_servicio_id}
          numero={visitando.numero}
          direccion={visitando.direccion}
          onCerrar={() => setVisitando(null)}
          onCotizar={() => {
            setCotizando(visitando)
            setVisitando(null)
          }}
        />
      )}

      {cotizando && (
        <Cotizador
          abierto
          enLaCalle
          ordenServicioId={cotizando.orden_servicio_id}
          ordenNumero={cotizando.numero}
          clienteNombre={cotizando.cliente ?? 'Sin cliente'}
          descripcionTrabajo={cotizando.descripcion}
          onCerrar={() => setCotizando(null)}
        />
      )}
    </section>
  )
}
